import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useBranding } from "../lib/branding";

export default function Setup() {
  const navigate = useNavigate();
  const branding = useBranding();
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/setup/status");
        if (cancelled) return;
        if (!data.needs_setup) {
          navigate("/superadmin/login", { replace: true });
          return;
        }
        setChecking(false);
      } catch {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 10) { setErr("Password must be at least 10 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await api.post("/setup/superadmin", { email: email.trim(), password });
      setDone(true);
      setTimeout(() => navigate("/superadmin/login", { replace: true }), 1800);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Setup failed");
    }
    setSubmitting(false);
  };

  if (checking) {
    return (
      <div className="auth-shell" style={{ alignItems: "center", justifyContent: "center" }}>
        <span className="text-sm" style={{ color: "var(--pg-text-dim)", position: "relative", zIndex: 2 }} data-testid="setup-checking">
          Checking platform status…
        </span>
      </div>
    );
  }

  return (
    <div
      className="auth-shell"
      data-testid="setup-shell"
      style={{
        backgroundImage:
          "linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.10) 40%, rgba(255,255,255,0.35) 100%), url('/login-bg-arches.jpg')",
        backgroundSize: "auto, cover",
        backgroundPosition: "center, center",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundAttachment: "fixed, fixed",
      }}
    >
      <div className="auth-brand">
        <div className="auth-brand-logo">
          <img src="/pragma-login-logo-v4.png" alt="Pragma CRM" className="auth-brand-logo-img" />
        </div>

        <div>
          <h1 className="auth-brand-headline">
            One last step <br />
            <span className="auth-brand-headline-accent">before launch.</span>
          </h1>
          <p className="auth-brand-tagline">
            Create the platform owner account. This is the only account that can manage tenants and platform-wide branding.
          </p>
        </div>

        <div className="auth-brand-footer">{branding.footer_text}</div>
      </div>

      <div className="auth-card-wrap">
        <div className="auth-card" data-testid="setup-card">
          {done ? (
            <div data-testid="setup-success">
              <span className="auth-card-badge">SUPERADMIN</span>
              <h2 className="auth-card-title">You're set.</h2>
              <p className="auth-card-sub">Redirecting you to the operations portal…</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="setup-form">
              {branding.logo_data_url && (
                <img
                  src={branding.logo_data_url}
                  alt="logo"
                  style={{ maxHeight: 36, maxWidth: 160, marginBottom: "1rem", objectFit: "contain" }}
                />
              )}
              <span className="auth-card-badge" data-testid="setup-badge">FIRST-RUN SETUP</span>
              <h2 className="auth-card-title">Create your Superadmin</h2>
              <p className="auth-card-sub">
                This one-time form initialises the platform owner account. You won't see this page again.
              </p>

              <label className="auth-label" htmlFor="setup-email-input">Email</label>
              <input
                id="setup-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                placeholder="you@yourdomain.com"
                data-testid="setup-email"
                required
                autoComplete="email"
              />

              <label className="auth-label" htmlFor="setup-password-input">Password (min. 10 chars)</label>
              <input
                id="setup-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                placeholder="••••••••••"
                data-testid="setup-password"
                required
                autoComplete="new-password"
                minLength={10}
              />

              <label className="auth-label" htmlFor="setup-confirm-input">Confirm password</label>
              <input
                id="setup-confirm-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="auth-input"
                placeholder="••••••••••"
                data-testid="setup-confirm"
                required
                autoComplete="new-password"
                minLength={10}
              />

              {err && <div className="auth-error" data-testid="setup-error">{err}</div>}

              <button
                type="submit"
                className="auth-submit"
                disabled={submitting}
                data-testid="setup-submit"
              >
                {submitting ? "Creating…" : "Create Superadmin"}
              </button>

              <p className="auth-footnote">
                Tip: store these credentials in your password manager.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
